import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { columnFilterSchema } from "../../../common/columnFilterSchema";
import { cursorSchema } from "../../../common/cursorSchema";
import { statsSchema } from "../../../common/statsSchema";
import { tagsSchema, tagsEnhancedSchema } from "../../../common/tagsSchema";

const advertisementStatsSchema = z.object({
  serving_hours: z.number().nullable(),
  serving_hours_prev: z.number().nullable(),
  serving_hours_delta: z.number().nullable(),
  serving_hours_percent: z.number().nullable(),

  serving_hours_cnt: z.number().nullable(),
  serving_hours_cnt_prev: z.number().nullable(),
  serving_hours_cnt_delta: z.number().nullable(),
  serving_hours_cnt_percent: z.number().nullable(),

  taov: z.number().nullable(),
  taov_prev: z.number().nullable(),
  taov_delta: z.number().nullable(),
  taov_percent: z.number().nullable(),

  top_of_search_impression_share: z.number().nullable(),
  top_of_search_impression_share_prev: z.number().nullable(),
  top_of_search_impression_share_delta: z.number().nullable(),
  top_of_search_impression_share_percent: z.number().nullable(),
});

export const adReportApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getV4AdvReportByAd",
    method: "get",
    path: "/v4/query/?table=advertisement&grouping=ad_id",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().array().optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "start_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "start_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "filters",
        type: "Query",
        schema: columnFilterSchema,
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "archived",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    status: 200,
    response: z.object({
      data: z.array(
        z
          .object({
            ad_id: z.number(),
            ad_name: z.string(),
            created_datetime: z.string().datetime({ local: true }),
            ad_goal: z.number(),
            state: z.number(),
            category_id: z.number(),
            ad_type: z.number(),
            auto_campaign_enabled: z.boolean(),
            non_trellis_enrolled_ad: z.boolean(),
            non_trellis_enrolled_ad_accept_bids: z.boolean(),
            ad_status: z.number(),
            days_active: z.number(),
            daily_budget: z.coerce.number(),
            monthly_budget: z.coerce.number(),
            daily_budget_override: z.number().nullable(),
            ad_source: z.string(),
            managed: z.string(),
            is_composite: z.boolean().optional(),
            serving_status: z.string(),

            max_acos: z.coerce.number(),
            target_acos: z.coerce.number(),
            min_roas: z.coerce.number(),
            target_roas: z.coerce.number(),

            min_bid: z.coerce.number().nullable(),
            max_bid: z.coerce.number().nullable(),

            trend_up: z.boolean(),
            trend_down: z.boolean(),
            serving_low: z.boolean(),
            recent: z.boolean(),
            high_acos: z.boolean().optional(),
            low_acos: z.boolean().optional(),
            in_range_acos: z.boolean().optional(),
            active_ad: z.boolean(),
            tags: tagsSchema,
            tags_enhanced: tagsEnhancedSchema,
            scheduled_actions: z.array(
              z.object({
                scheduled_action_start_date: z.string().optional(),
                scheduled_action_end_date: z.string().optional(),
                scheduled_action_fields: z.string().transform((spec) => {
                  try {
                    return JSON.parse(spec) as {
                      change_type?: string;
                      change_value?: number;
                    }[];
                  } catch {
                    return [];
                  }
                }),
                scheduled_action_status: z.string().optional(),
              }),
            ),
            ntm_enrollment_status: z.string().nullable(),
            program_id: z.number().nullable(),
            program_name: z.string().nullable(),
            day_parting_method: z.number().nullable(),
            bid_algo: z.string().nullable(),
            multi_marketplace: z.boolean().optional(),
          })
          .merge(statsSchema)
          .merge(advertisementStatsSchema),
      ),
      cursor: cursorSchema,
      count: z.number(),
      count_details: z.object({
        count_total: z.number(),
        count_distinct: z.number(),
        trend_up_count: z.number(),
        trend_down_count: z.number(),
        serving_low_count: z.number(),
        recent_count: z.number(),
        high_acos_count: z.number().optional(),
        low_acos_count: z.number().optional(),
        in_range_acos_count: z.number().optional(),
        active_ad_count: z.number().optional(),
      }),
    }),
  })
    .addEndpoint({
      alias: "getV4AdReportExport",
      method: "get",
      path: "/v4/query/?table=advertisement&xls=true&report_type=ad_report",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
        {
          name: "archived",
          type: "Query",
          schema: z.boolean().optional(),
        },
      ],
      status: 200,
      response: z.instanceof(Blob),
    })
    .addEndpoint({
      alias: "getV4AdvReportByDate",
      method: "get",
      path: "/v4/query/?table=advertisement&grouping=start_date&limit=1000000&include_count=false",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "date_group",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z
            .object({
              start_date: z.string().date(),
            })
            .merge(statsSchema)
            .merge(advertisementStatsSchema),
        ),
        cursor: cursorSchema,
        count: z.number(),
      }),
    })
    .addEndpoint({
      alias: "getV4AdvReportTotals",
      method: "get",
      path: "/v4/query/aggregate/?table=advertisement",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.object({
        data: statsSchema.merge(advertisementStatsSchema),
      }),
    })
    .addEndpoint({
      alias: "getV4AdvReportOptions",
      method: "get",
      path: "/v4/query/values/?table=advertisement",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.record(z.array(z.string())),
      }),
    })
    .addEndpoint({
      alias: "getV4AdvReportByProgram",
      method: "get",
      path: "/v4/query/?table=advertisement&grouping=program_id",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z.object({
            program_id: z.number().nullable(),
            active_ad_count: z.number().nullable(),
          }),
        ),
      }),
    })
    .addEndpoint({
      alias: "getV4AdvReportByAdType",
      method: "get",
      path: "/v4/query/?table=advertisement&grouping=ad_type",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z
            .object({
              ad_type: z.number(),
              active_ad_count: z.number().nullable(),
            })
            .merge(statsSchema)
            .merge(advertisementStatsSchema),
        ),
      }),
    })
    .build(),
);
